import { Button, Popover } from 'antd';
import Down from 'assets/svg/Down';
import Minus from 'assets/svg/Minus';
import Plus from 'assets/svg/Plus';
import React, { useContext, useMemo, useState } from 'react';
import { trackEventMixpanel } from 'utils/mixpanel';
import { GLOBAL_FILTER_ATTRIBUTE_EXPAND_COLLAPSE, GLOBAL_FILTER_TIME_SELECTION } from 'utils/mixpanelEventNames';
import { default as CalenderIcon } from 'assets/svg/Calender';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import dayjs from 'dayjs';
import { FilterContextData } from 'contexts/FilterContext';
import { SelectionCapsul } from './FilterAttributeTypeList';
import CampaignCalender from '../CampaignCalender';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';

const CustomButton = styled(Button)`
    &.ant-btn[disabled] {
        ${tw`bg-borderGrey text-promoColour text-[13px] rounded font-bold`}
    }
    &.ant-btn:focus:not(:disabled),
    &.ant-btn:hover:not(:disabled) {
        ${tw`border-purpleFont text-white opacity-80 bg-purpleFont`}
    }
    ${tw`h-[40px] leading-[13px] rounded text-[13px] bg-purpleFont text-white font-bold`}
`;

interface Props {
    timePeriod: any[];
    attributeTypeExpandClose: any;
    setAttributeTypeExpandClose: any;
    timePeriodSelection: any;
    setTimePeriodSelection: Function;
    setTimePeriodList: Function;
    setTimeSelectionLabel: Function;
    timeSelectionLabel: string;
    latestDate: string | null;
}

export type DateRangeType = [Date | null, Date | null];

function CampaignFilterTimePeriod({
    timePeriod,
    attributeTypeExpandClose,
    setAttributeTypeExpandClose,
    timePeriodSelection,
    setTimePeriodSelection,
    setTimePeriodList,
    setTimeSelectionLabel,
    timeSelectionLabel,
    latestDate,
}: Props) {
    const { visibleDatePicker, setVisibleDatePicker, calenderStartDay } = useContext(FilterContextData);
    const [visibleTimePeriodList, setVisibleTimePeriodList] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [tempRange, setTempRange] = useState<any>();
    const [stateForCalender, setStateForCalender] = useState({ step: 1, dateRange: [null, null] });
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);

    //calender set date function
    const handleOpenChange = (
        newOpen: boolean,
        isClickOutside: boolean = true,
        from: 'header' | 'dateRange' = 'dateRange',
    ) => {
        setIsOpen(newOpen);
        if (!newOpen && !isClickOutside) {
            //only save the date when clicked on set date button
            setStateForCalender((prev: any) => ({ ...prev, dateRange: tempRange }));
            const utcOffset = dayjs().utcOffset();
            const startDateUnix = dayjs(tempRange[0]).add(utcOffset, 'm').unix();
            const endDateUnix = dayjs(tempRange[1]).add(utcOffset, 'm').unix();
            setTimePeriodSelection(
                JSON.stringify({
                    start_timestamp: startDateUnix,
                    end_timestamp: endDateUnix,
                }),
            );
            setTimePeriodList((oldData: any) => {
                let list: any = [];
                list = oldData.map((item: any) => {
                    let temp = { ...item };
                    if (item.timePeriod === 'Custom Date') {
                        temp.startTimestamp = startDateUnix;
                        temp.endTimestamp = endDateUnix;
                    }
                    return temp;
                });
                return list;
            });
        } else if (!newOpen) setTempRange([...stateForCalender.dateRange]); //if clicked outside dont save the change
    };

    const expandTimeZone = () => {
        setAttributeTypeExpandClose((oldValue: String) => (oldValue == 'timeZone' ? '' : 'timeZone'));
        //mixpanel event
        trackEventMixpanel(GLOBAL_FILTER_ATTRIBUTE_EXPAND_COLLAPSE, {
            isExpanded: attributeTypeExpandClose === 'timeZone' ? false : true,
            attributeType: 'Time Period',
            dimension: '',
        });
    };

    const showPeriodList = () => {
        setVisibleTimePeriodList(!visibleTimePeriodList);
    };

    //set function for time selection context except custom when there no data
    const setPeriod = (list: any) => {
        let jsonObj = JSON.parse(list.json);
        if (list.label === 'Custom Date' && jsonObj.start_timestamp == 0) {
            setVisibleDatePicker(true);
        } else {
            setTimePeriodSelection(list.json);
            setVisibleDatePicker(() => (list.label === 'Custom Date' ? true : false));
        }
        setTimeSelectionLabel(list.label);
        //mixpanel event
        trackEventMixpanel(GLOBAL_FILTER_TIME_SELECTION, {
            selectedDate: list,
        });
    };

    const handleDateChange = (range: DateRangeType) => {
        setTempRange([...range]);
    };

    const dateLabel = useMemo(() => {
        let startLabel = 'Start Date';
        let endLabel = 'End Date';
        const { dateRange } = stateForCalender;
        if (dateRange[0]) startLabel = dayjs(new Date(dateRange[0])).format(commonConfig?.dateFormat);
        if (dateRange[1]) endLabel = dayjs(new Date(dateRange[1])).format(commonConfig?.dateFormat);
        return { startLabel, endLabel };
    }, [stateForCalender]);

    return (
        <div
            className={`flex-col items-center justify-between px-3.2 pb-3.2 border-b border-borderLine font-sm ${
                attributeTypeExpandClose === 'timeZone' ? 'bg-lightPurpleBg border-t' : ''
            }`}
        >
            <div className={`flex items-center justify-between pt-3 `} onClick={() => expandTimeZone()}>
                <span>Time Period</span>
                <span>{attributeTypeExpandClose === 'timeZone' ? <Minus /> : <Plus />}</span>
            </div>
            {attributeTypeExpandClose === 'timeZone' && (
                <>
                    <div className="relative mt-4">
                        <div onClick={() => showPeriodList()}>
                            <div className="px-3 py-1 border rounded border-purpleFont border-0 bg-white flex items-center justify-between">
                                <input
                                    type="text"
                                    className=" font-xs focus:outline-none  w-full"
                                    value={
                                        timeSelectionLabel && timeSelectionLabel?.length < 5
                                            ? timeSelectionLabel.toUpperCase()
                                            : timeSelectionLabel
                                    }
                                />
                                <span className="z-10 font-normal  text-center text-slate-300 text-base w-8 mr-[-10px]">
                                    <Down />
                                </span>
                            </div>

                            {visibleTimePeriodList && (
                                <div className="bg-white pt-2 pb-2 z-30 absolute w-full drop-shadow-lg rounded-b cursor-pointer">
                                    {timePeriod.map((list: any, key: Number) => (
                                        <div
                                            key={list.timePeriod.toUpperCase()}
                                            className={`px-2 py-1 ${
                                                timeSelectionLabel?.toUpperCase() === list.timePeriod.toUpperCase() &&
                                                'bg-bgBackground'
                                            }`}
                                            onClick={() =>
                                                setPeriod({
                                                    label: list.timePeriod,
                                                    json: JSON.stringify({
                                                        start_timestamp: list.startTimestamp,
                                                        end_timestamp: list.endTimestamp,
                                                    }),
                                                })
                                            }
                                        >
                                            {list.timePeriod.length < 5
                                                ? list.timePeriod.toUpperCase()
                                                : list.timePeriod}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    {visibleDatePicker && (
                        <div className="mt-4 ">
                            <Popover
                                trigger="click"
                                open={isOpen}
                                onOpenChange={handleOpenChange}
                                placement={'right'}
                                // overlayInnerStyle={{ padding: 0 }}
                                content={
                                    <CampaignCalender
                                        disabledDates={[null, latestDate]}
                                        startDay={calenderStartDay}
                                        range={tempRange}
                                        onChange={handleDateChange}
                                        onFooterClick={() => handleOpenChange(false, false)}
                                        singleDateSelect
                                    />
                                }
                            >
                                <div className="flex justify-center items-center w-full">
                                    <div className="cursor-pointer w-full flex justify-start gap-1 py-1 items-center px-1 border border-purpleFont rounded">
                                        <CalenderIcon />
                                        <div className="flex w-full justify-center">
                                            <span
                                                className={`${
                                                    !stateForCalender.dateRange.includes(null) && !isOpen
                                                        ? 'text-subHeaderColor'
                                                        : 'text-borderGrey'
                                                }  font-xs`}
                                            >
                                                {dateLabel.startLabel}
                                            </span>
                                            <span
                                                className={`${
                                                    !stateForCalender.dateRange.includes(null) && !isOpen
                                                        ? 'text-subHeaderColor'
                                                        : 'text-borderGrey'
                                                } mx-1  font-xs`}
                                            >
                                                -
                                            </span>
                                            <span
                                                className={`${
                                                    !stateForCalender.dateRange.includes(null) && !isOpen
                                                        ? 'text-subHeaderColor'
                                                        : 'text-borderGrey'
                                                } font-xs`}
                                            >
                                                {dateLabel.endLabel}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Popover>
                        </div>
                    )}
                </>
            )}
            {attributeTypeExpandClose !== 'timeZone' && (
                <SelectionCapsul>
                    {timeSelectionLabel === 'Custom Date'
                        ? dateLabel.startLabel + ' - ' + dateLabel.endLabel
                        : timeSelectionLabel.toUpperCase()}
                </SelectionCapsul>
            )}
        </div>
    );
}

export default CampaignFilterTimePeriod;
